export class MultipartUploader {
  constructor({
    file,
    fileName,
    uploadId,
    fileKey,
    urls = [],
    chunkSize = 1024 * 1024 * 5,
    threadsQuantity = 5
  }) {
    // this must be bigger than or equal to 5MB,
    // otherwise AWS will respond with:
    // "Your proposed upload is smaller than the minimum allowed size"
    this.chunkSize = chunkSize || 1024 * 1024 * 5;
    // number of parallel uploads
    this.threadsQuantity = Math.min(threadsQuantity || 5, 15);
    this.file = file;
    this.fileName = fileName;
    this.numberOfparts = Math.ceil(this.file.size / this.chunkSize);
    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = urls;
    this.uploadedParts = [];
    this.fileId = uploadId;
    this.fileKey = fileKey;

    this.onProgressFn = () => {
      return null;
    };
    this.onErrorFn = () => {
      return null;
    };
    this.sendCompleteRequestFn = () => {
      return null;
    };
  }

  // starting the multipart upload request
  async start() {
    await this.initialize();
  }

  getNumberOfParts() {
    return this.numberOfparts;
  }

  async initialize() {
    try {
      // retrieving the pre-signed URLs
      this.sendNext();
    } catch (error) {
      await this.complete(error);
    }
  }

  sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }

    if (!this.parts.length) {
      if (!activeConnections) {
        this.complete();
      }

      return;
    }

    const part = this.parts.pop();
    if (this.file && part) {
      const sentSize = (part.partNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext();
      };

      this.sendChunk(chunk, part, sendChunkStarted)
        .then(() => {
          this.sendNext();
        })
        .catch((error) => {
          this.parts.push(part);

          this.complete(error);
        });
    }
  }

  // terminating the multipart upload request on success or failure
  async complete(error) {
    if (error && !this.aborted) {
      this.onErrorFn(error);
      return;
    }

    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
      // eslint-disable-next-line no-shadow
    } catch (error) {
      this.onErrorFn(error);
    }
  }

  sendChunk(chunk, part, sendChunkStarted) {
    return new Promise((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error('Failed chunk upload'));
            return;
          }

          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // calculating the current progress of the multipart upload request
  handleProgress(part, event) {
    if (this.file) {
      if (event.type === 'progress' || event.type === 'error' || event.type === 'abort') {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === 'uploaded') {
        this.uploadedSize += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => memo + this.progressCache[id], 0);

      const sent = Math.min(this.uploadedSize + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);
      this.onProgressFn({
        sent: sent,
        total: total,
        percentage: percentage
      });
    }
  }

  // uploading a part through its pre-signed URL
  upload(file, part, sendChunkStarted) {
    // uploading each part with its pre-signed URL
    return new Promise((resolve, reject) => {
      if (this.fileId && this.fileKey) {
        // eslint-disable-next-line no-multi-assign
        const xhr = (this.activeConnections[part.partNumber - 1] = new XMLHttpRequest());

        sendChunkStarted();

        const progressListener = this.handleProgress.bind(this, part.partNumber - 1);

        xhr.upload.addEventListener('progress', progressListener);
        xhr.addEventListener('error', progressListener);
        xhr.addEventListener('abort', progressListener);
        xhr.addEventListener('loadend', progressListener);

        xhr.open('PUT', part.url);
        xhr.onreadystatechange = () => {
          if (xhr.readyState === xhr.HEADERS_RECEIVED && xhr.status === 200) {
            // retrieving the ETag parameter from the HTTP headers
            const ETag = xhr.getResponseHeader('ETag');
            if (ETag) {
              const uploadedPart = {
                PartNumber: part.partNumber,
                // removing the " enclosing carachters from  the raw ETag
                ETag: ETag.replaceAll('"', '')
              };

              this.uploadedParts.push(uploadedPart);

              resolve(xhr.status);
              delete this.activeConnections[part.partNumber - 1];
            }
          }
        };

        xhr.onerror = (error) => {
          reject(error);
          delete this.activeConnections[part.partNumber - 1];
        };

        xhr.onabort = () => {
          reject(new Error('Upload canceled by user'));
          delete this.activeConnections[part.partNumber - 1];
        };

        xhr.send(file);
      }
    });
  }

  // finalizing the multipart upload request on success by calling
  // the finalization API
  async sendCompleteRequest() {
    this.sendCompleteRequestFn({
      fileId: this.fileId,
      fileKey: this.fileKey,
      bucket: this.bucket,
      parts: this.uploadedParts
    });
  }

  onProgress(onProgress) {
    this.onProgressFn = onProgress;
    return this;
  }

  onComplete(sendCompleteRequestFn) {
    this.sendCompleteRequestFn = sendCompleteRequestFn;
    return this;
  }

  withParts(parts) {
    this.parts = parts;
    return this;
  }

  withUploadId(uploadId) {
    this.fileId = uploadId;
    return this;
  }

  withKey(fileKey) {
    this.fileKey = fileKey;
    return this;
  }

  withBuket(bucket) {
    this.bucket = bucket;
    return this;
  }

  onError(onError) {
    this.onErrorFn = onError;
    return this;
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        this.activeConnections[id].abort();
      });

    this.aborted = true;
  }
}
